<template>
  <pf-table v-if="props.customers.length" striped v-bind="ouiaProps">
    <pf-thead>
      <pf-tr>
        <pf-th
          ><sort-link v-model="s" v-model:reverse="r" field="ragione_sociale">{{ $t('Nome cliente') }}</sort-link></pf-th
        >
        <pf-th>{{ $t('Partita Iva') }}</pf-th>
        <pf-th>{{ $t('Indirizzo') }}</pf-th>
        <pf-th>{{ $t('Provincia') }}</pf-th>
        <pf-th>{{ $t('Stato') }}</pf-th>
        <pf-th>{{ $t('Contatto') }}</pf-th>
        <pf-th :screen-reader-text="$t('Azioni')" />
      </pf-tr>
    </pf-thead>
    <pf-tbody v-if="filtered.length">
      <pf-tr v-for="customer in filtered" :key="customer.id">
        <pf-td :data-label="$t('Nome cliente')"
          ><span v-if="customer.ragione_sociale">{{ customer.ragione_sociale }}</span></pf-td
        >
        <pf-td :data-label="$t('Partita Iva')"
          ><span v-if="customer.partita_iva">{{ customer.partita_iva }}</span></pf-td
        >
        <pf-td :data-label="$t('Indirizzo')"
          ><span v-if="customer.indirizzo">{{ customer.indirizzo }}</span></pf-td
        >
        <pf-td :data-label="$t('Provincia')"
          ><span v-if="customer.cap">{{ customer.cap }}</span
          >{{ ' ' }}<span v-if="customer.provincia">{{ customer.provincia }}</span></pf-td
        >
        <pf-td :data-label="$t('Stato')">{{ customer.stato }}</pf-td>
        <pf-td :data-label="$t('Contatto')"
          >{{ customer.contact_name }} <a v-if="customer.contact_mail" :href="`mailto:${customer.contact_mail}`">{{ customer.contact_mail }}</a></pf-td
        >
        <pf-td class="pf-v6-u-text-align-end-on-md">
          <pf-button
            v-if="customer.contact_mail"
            :disabled="loadingRequestAccess || loadingLogin"
            small
            @click="wu(customer.id, customer.contact_mail)"
            variant="secondary"
            class="pf-v6-u-mr-sm"
            :ouia-id="`send-welcome-button-${customer.id}`"
          >
            {{ $t('Invia benvenuto') }}
          </pf-button>
          <pf-button
            v-if="customer.login"
            :disabled="loadingRequestAccess || loadingLogin"
            small
            @click="login(customer.id)"
            :data-ouia-component-id="`button-login-${customer.id}`"
          >
            {{ $t('Log In') }}
          </pf-button>
          <pf-button
            v-else
            :disabled="loadingRequestAccess || loadingLogin"
            variant="primary"
            small
            @click="requestAccess(customer.id, customer.ragione_sociale)"
          >
            {{ $t('Richiedi accesso') }}
          </pf-button>
        </pf-td>
      </pf-tr>
    </pf-tbody>
    <pf-tbody v-else>
      <pf-td colspan="6">
        <pf-empty-state>
          <pf-empty-state-header :title="$t('Nessun cliente trovato')" heading-level="h4">
            <template #icon>
              <magnifying-glass-icon />
            </template>
          </pf-empty-state-header>
        </pf-empty-state>
      </pf-td>
    </pf-tbody>
  </pf-table>

  <pf-page-section v-else>
    <pf-empty-state>
      <pf-empty-state-header :title="$t('Nessun cliente assegnato')" heading-level="h4">
        <template #icon>
          <magnifying-glass-icon />
        </template>
      </pf-empty-state-header>
      <pf-empty-state-body>
        <pf-empty-state-actions>
          <pf-button variant="primary" href="#crea_prospect">{{ $t("Crea un'azienda prospect") }}</pf-button>
        </pf-empty-state-actions>
      </pf-empty-state-body>
    </pf-empty-state>
  </pf-page-section>

  <x-form
    v-if="welcomeEmailState.state === 'success' && welcomeEmail"
    modal
    :title="$t('Invia mail di benvenuto')"
    variant="medium"
    :open="welcomeUser !== null"
    :submit-text="$t('Invia la mail')"
    @close="welcomeUser = null"
    @submit="sendWelcomeEmail()"
  >
    <template #description>
      <p>
        {{
          $t('Qui sotto puoi leggere il testo della mail che invieremo al tuo cliente con il link per resettare la password e accedere alla sua area cliente.')
        }}
      </p>
    </template>

    <form-group :name="$t('Oggetto')">
      <x-input :model-value="welcomeEmail.subject" readonly disabled ouia-id="welcome-email-subject" />
    </form-group>
    <form-group :name="$t('Messaggio')">
      <pf-textarea :model-value="welcomeEmail.body" auto-resize readonly disabled ouia-id="welcome-email-body" />
    </form-group>
  </x-form>
</template>

<script lang="ts" setup>
import { type Ref, ref, computed } from 'vue';
import { $t as _t } from '@/i18n';
import { http } from '../http';
import { useRouter, useRoute } from 'vue-router';
import { type CompanyAccess, type MyCustomersAccess, WelcomeEmailResource } from '@/resources';
import { useLoginStore } from '@/store/login';
import { loginDestination } from '../utils';
import SortLink from '../components/sort-link.vue';
import { any, by } from '@common/utils';
import MagnifyingGlassIcon from '@vue-patternfly/icons/magnifying-glass-icon';
import { useFunctionRunningState } from '@common/use';
import { asyncComputed } from '@common/asyncComputed';
import { useOUIAProps, type OUIAProps } from '@common/ouia';

const props = defineProps<
  OUIAProps & {
    customers: ({ login: CompanyAccess } & MyCustomersAccess)[];
    filter: string;
  }
>();
const ouiaProps = useOUIAProps(props);

const router = useRouter();
const route = useRoute();

const s = ref<keyof MyCustomersAccess>('ragione_sociale'); // sort by
const r = ref(false); // reverse sort

const welcomeUser: Ref<{ idAzienda: number; email: string } | null> = ref(null);

const filtered = computed(() => props.customers.filter(any(props.filter)).sort(by(s.value, r.value)));

const { isRunning: loadingRequestAccess, run: requestAccess } = useFunctionRunningState(
  async (id: number, azienda: string) => {
    if (
      !window.confirm(
        _t(
          "Vuoi poter accedere all'area riservata di {0}? Confermando invieremo un'email di attivazione all'azienda, riceverai una notifica se la tua richiesta verrà accettata.",
          [azienda],
        ),
      )
    ) {
      return;
    }

    const data = new FormData();
    data.append('id', String(id));
    await http.post('/rest/my_customers/requestaccess', data);
  },
  { preventMultiple: true },
);

const { isRunning: loadingLogin, run: login } = useFunctionRunningState(
  async (id: number) => {
    const data = new FormData();
    data.append('id_azienda', String(id));

    const result = (await http.post('/api/auth/companylogin', data)).data.result;

    if (result == 'ok') {
      const login = useLoginStore();
      login.refresh();
      router.push(loginDestination(route.query));
    }
  },
  { preventMultiple: true },
);

function sendWelcomeEmail() {
  if (welcomeUser.value !== null) {
    new WelcomeEmailResource().post({ idAzienda: welcomeUser.value.idAzienda, email: welcomeUser.value.email });
    welcomeUser.value = null;
  }
}

function wu(idAzienda: number, email: string) {
  welcomeUser.value = { idAzienda, email };
}

const { state: welcomeEmailState, result: welcomeEmail } = asyncComputed(async () => {
  if (welcomeUser.value) {
    return await new WelcomeEmailResource({ idAzienda: welcomeUser.value.idAzienda }).get(welcomeUser.value.email);
  }
  return null;
});
</script>
